import { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import "../css/MitgliedVerwaltung.css";
import {
  changeRoleApi,
  deleteMitgliedApi,
  fetchMitgliederApi,
  resetPasswordApi,
} from "../api/MitgliedApi";
import { Mitglied } from "../types/Mitglied";

const MitgliederVerwaltung = () => {
  const { user } = useContext(AuthContext)!;
  const [mitglieder, setMitglieder] = useState<Mitglied[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // 1. FIX: loadData in useCallback verpackt
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMitgliederApi(search, page, size);
      setMitglieder(data.content);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error("Fehler beim Laden:", err);
    } finally {
      setLoading(false);
    }
  }, [search, page, size]); // Abhängigkeiten von loadData

  // 2. FIX: loadData als einzige Abhängigkeit im Effekt
  useEffect(() => {
    loadData();
  }, [loadData]); 

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Soll dieses Mitglied wirklich unwiderruflich gelöscht werden?",
      )
    ) {
      try {
        await deleteMitgliedApi(id);
        loadData();
      } catch (e) {
        alert("Fehler beim Löschen");
      }
    }
  };

  const handleResetPassword = async (id: number) => {
    const newPassword = prompt("Bitte neues Passwort eingeben:");
    if (newPassword) {
      try {
        await resetPasswordApi(id, newPassword);
        alert("Passwort wurde erfolgreich geändert.");
      } catch (e) {
        alert("Fehler beim Ändern des Passworts");
      }
    }
  };

  const handleChangeRole = async (id: number) => {
    const newRole = prompt("Neue Rolle eingeben (USER / ADMIN):");
    if (!newRole) return;

    try {
      await changeRoleApi(id, newRole);
      loadData();
    } catch (e) {
      alert("Fehler beim Ändern der Rolle");
    }
  };

  return (
    <div className="page-container">
      <div className="header-glass">
        <h1>Mitgliederverwaltung</h1>
        <p className="subtitle">
          Admin-Bereich: {user?.vorname} {user?.nachname}
        </p>
      </div>

      <div className="glass-controls">
        <input
          type="text"
          className="glass-search"
          placeholder="🔍 Suche nach Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && loadData()}
        />
        <button className="btn-primary" onClick={loadData}>
          Suchen
        </button>
      </div>

      {loading ? (
        <div className="loading-glass">Lade Daten...</div>
      ) : (
        <div className="table-wrapper">
          <table className="glass-table">
            <thead>
              <tr>
                <th>ID / User</th>
                <th>Kontakt</th>
                <th>Status</th>
                <th>Rolle</th>
                <th style={{ textAlign: "right" }}>Verwaltung</th>
              </tr>
            </thead>
            <tbody>
              {mitglieder.map((m) => (
                <tr key={m.mitgliedID}>
                  <td>
                    <div className="user-cell">
                      <span className="user-name">{m.username}</span>
                      <span className="user-id">#{m.mitgliedID}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <span>
                        {m.vorname} {m.nachname}
                      </span>
                      <small>{m.email}</small>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${m.mitgliedsstatus.toLowerCase()}`}
                    >
                      {m.mitgliedsstatus}
                    </span>
                  </td>
                  <td>{m.roles.join(", ")}</td>
                  <td className="actions-cell">
                    <button
                      className="btn-icon warning"
                      title="Passwort Reset"
                      onClick={() => handleResetPassword(m.mitgliedID)}
                    >
                      PW
                    </button>
                    <button
                      className="btn-icon info"
                      title="Rolle ändern"
                      onClick={() => handleChangeRole(m.mitgliedID)}
                    >
                      Rolle
                    </button>
                    <button
                      className="btn-icon danger"
                      title="Löschen"
                      onClick={() => handleDelete(m.mitgliedID)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pagination-glass">
        <button disabled={page === 0} onClick={() => setPage(page - 1)}>
          &larr; Zurück
        </button>
        <span className="page-info">
          Seite {page + 1} von {totalPages}
        </span>
        <button
          disabled={page + 1 >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Weiter &rarr;
        </button>
      </div>
    </div>
  );
};

export default MitgliederVerwaltung;